import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { SidebarMenuItem } from '@/components/ui-kit/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui-kit/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { SidebarMenuGroupProps } from '@/types/sidebar.type';
import { MenuIcon, SIDEBAR_NAV_ICON_SIZE } from '@/components/core';
import { cn } from '@/lib/utils';
import { matchesSidebarPath } from '@/lib/utils/sidebar-utils';
import {
  navIconClass,
  navItemClass,
  navLabelClass,
  navSubItemClass,
} from '../app-sidebar/nav-styles';

/**
 * A sidebar group (Time & Attendance, Time Off, ...): a header that only opens and closes, with its
 * pages under it. Open/closed is owned by AppSidebar, so only one group is open at a time and the group
 * of the current page opens after navigating. In the collapsed rail the group is an icon with its label
 * under it, and a click opens its pages in a menu to the right (HR Module Prototype, Component Map).
 */
export const SidebarMenuGroupComponent = ({
  title,
  icon,
  items,
  isRail,
  isOpen,
  onToggle,
  onItemClick,
}: Readonly<SidebarMenuGroupProps>) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // When a page's path is a prefix of a sibling's, only the longest (most specific) match is current.
  const activeChildId = items
    .filter((child) => matchesSidebarPath(pathname, child.path))
    .sort((a, b) => b.path.length - a.path.length)[0]?.id;
  const isGroupActive = Boolean(activeChildId);

  const groupIcon = (
    <MenuIcon
      name={icon}
      size={SIDEBAR_NAV_ICON_SIZE}
      className={navIconClass({ active: isGroupActive, rail: Boolean(isRail) })}
    />
  );

  if (isRail) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={navItemClass({ active: isGroupActive, rail: true })}
              data-active={isGroupActive}
            >
              {groupIcon}
              <span className={navLabelClass({ active: isGroupActive, rail: true })}>
                {t(title)}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="min-w-48">
            <DropdownMenuLabel>{t(title)}</DropdownMenuLabel>
            {items.map((child) => (
              <DropdownMenuItem
                key={child.id}
                className={cn(child.id === activeChildId && 'font-semibold')}
                aria-current={child.id === activeChildId ? 'page' : undefined}
                onSelect={() => {
                  navigate(child.path);
                  onItemClick?.();
                }}
              >
                {t(child.name)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle} asChild>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            // Its page is open but the group is closed: the group reads as the current place.
            className={navItemClass({ active: isGroupActive && !isOpen, rail: false })}
            data-active={isGroupActive}
          >
            {groupIcon}
            <span className={navLabelClass({ active: isGroupActive && !isOpen, rail: false })}>
              {t(title)}
            </span>
            <ChevronRight
              aria-hidden
              className={cn(
                'size-3.5 shrink-0 text-muted-foreground transition-transform duration-150',
                isOpen && 'rotate-90'
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="mb-1.5 flex flex-col">
            {items.map((child) => {
              const isChildActive = child.id === activeChildId;
              return (
                <li key={child.id}>
                  <Link
                    to={child.path}
                    className={navSubItemClass(isChildActive)}
                    onClick={onItemClick}
                    aria-current={isChildActive ? 'page' : undefined}
                  >
                    <span className="min-w-0 truncate">{t(child.name)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
